import React, { useEffect, useMemo, useState } from 'react';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import CookieConsent from './CookieConsent';
import { CONSENT_KEY, CONSENT_GRANTED, CONSENT_DENIED, OPEN_CONSENT_EVENT } from '../constants/consent';

function getStoredConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY);
  } catch {
    return null;
  }
}

function setStoredConsent(val) {
  try {
    localStorage.setItem(CONSENT_KEY, val);
  } catch {
    /* no-op */
  }
}

export default function AnalyticsGate({ children }) {
  const [consent, setConsent] = useState(() => getStoredConsent());
  const [forceOpen, setForceOpen] = useState(false);

  useEffect(() => {
    const openBanner = () => setForceOpen(true);
    window.addEventListener(OPEN_CONSENT_EVENT, openBanner);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, openBanner);
  }, []);

  // PostHog är redan igång om samtycke getts tidigare i sessionen - då måste vi
  // slå av/på insamlingen explicit, det räcker inte att avmontera providern.
  const accept = () => {
    setStoredConsent(CONSENT_GRANTED);
    setConsent(CONSENT_GRANTED);
    setForceOpen(false);
    if (posthog.__loaded) posthog.opt_in_capturing();
  };
  const decline = () => {
    setStoredConsent(CONSENT_DENIED);
    setConsent(CONSENT_DENIED);
    setForceOpen(false);
    if (posthog.__loaded) posthog.opt_out_capturing();
  };

  const posthogOptions = useMemo(() => ({
    api_host: process.env.REACT_APP_POSTHOG_HOST,
    defaults: '2025-05-24',
    capture_exceptions: true,
    debug: process.env.NODE_ENV === 'development',
    // Respect Do Not Track in addition to consent
    respect_dnt: true,
    // Don't load if user denied (extra safety)
    loaded: (ph) => {
      if (consent !== CONSENT_GRANTED) {
        ph.opt_out_capturing();
      }
    },
  }), [consent]);

  // Visa om inget val gjorts ännu, eller om den öppnats via länken i footern (oavsett tidigare val)
  const undecided = consent !== CONSENT_GRANTED && consent !== CONSENT_DENIED;
  const showBanner = undecided || forceOpen;

  // If undecided (or reopened via the footer link), show banner and do not load PostHog
  if (consent !== CONSENT_GRANTED) {
    return (
      <>
        {children}
        {showBanner && <CookieConsent onAccept={accept} onDecline={decline} />}
      </>
    );
  }

  // Consent granted: render PostHogProvider
  return (
    <PostHogProvider apiKey={process.env.REACT_APP_POSTHOG_KEY} options={posthogOptions}>
      {children}
      {showBanner && <CookieConsent onAccept={accept} onDecline={decline} />}
    </PostHogProvider>
  );
}
